import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { emit } from '@/lib/admin/telemetry';
import { SUPPORT_EMAIL, SUPPORT_MAILTO } from '@/config/support';

export type WelcomeStep = 'invite' | 'conversation' | 'theme';

interface Props {
  firstName: string;
  orgId: string;
  userId: string;
  steps: { invite: boolean; conversation: boolean; theme: boolean };
}

const STEPS: Array<{
  key: WelcomeStep;
  title: string;
  cta: string;
  to: string;
}> = [
  { key: 'invite', title: 'Invite your team', cta: 'Invite employees', to: '/app/admin/people?invite=1' },
  { key: 'conversation', title: 'Launch your first Charles conversation', cta: 'Start a conversation', to: '/app/admin/surveys/new' },
  { key: 'theme', title: 'Set your first theme', cta: 'Choose a theme', to: '/app/admin/insights/themes' },
];

export function WelcomeState({ firstName, orgId, userId, steps }: Props) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-heading font-semibold tracking-tight">
          Welcome to CultureLens, {firstName}.
        </h2>
        <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
          Your organization is live. Here's how to get your first signal in the next 10 minutes.
        </p>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        {STEPS.map((s, i) => {
          const done = steps[s.key];
          return (
            <Card key={s.key} className={cn('relative overflow-hidden', done && 'bg-muted/40')}>
              <CardContent className="p-5 flex flex-col gap-4 h-full">
                <div className="flex items-center gap-3">
                  <div
                    className={cn(
                      'h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold',
                      done
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground',
                    )}
                  >
                    {done ? <Check className="h-4 w-4" /> : i + 1}
                  </div>
                  <div className="text-sm font-medium leading-tight">{s.title}</div>
                </div>
                <div className="mt-auto">
                  <Button
                    asChild
                    size="sm"
                    variant={done ? 'outline' : 'default'}
                    className="w-full justify-between"
                  >
                    <Link
                      to={s.to}
                      onClick={() =>
                        emit({
                          name: 'welcome_cta_click',
                          step: s.key,
                          org_id: orgId,
                          user_id: userId,
                          timestamp: new Date().toISOString(),
                        })
                      }
                    >
                      {done ? 'Done' : s.cta}
                      <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground">
        Need help?{' '}
        <a href={SUPPORT_MAILTO} className="underline">{SUPPORT_EMAIL}</a>
      </p>
    </div>
  );
}
